import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Card } from '../components/SubscriptionAppCard';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { Input } from '../components/SubscriptionAppInput';
import { StatCard } from '../components/StatCard';
import { TagInput } from '../components/TagInput';
import type { TagData } from '../components/TagInput';
import { OrdersTable } from '../components/OrdersTable';
import { ActivityTimeline } from '../components/subscription-detail/ActivityTimeline';
import { PaymentMethodModal, PaymentData } from '../components/subscription-detail/PaymentMethodModal';
import { ProductSelector } from '../components/subscription-detail/ProductSelector';
import { CreateTaskModal } from '../components/CreateTaskModal';
import type { TaskData } from '../components/CreateTaskModal';
import { subscriptions, availableProducts, getItemPrice } from '../data/mockSubscriptionData';
import { useActivities } from '../contexts/ActivityContext';
import { useTasks } from '../contexts/TaskContext';
import { useTags } from '../contexts/TagsContext';
import { ArrowLeft, ExternalLink, User as UserIcon, Edit, Save, X, Mail, Phone, MapPin, Copy, Check, DollarSign, TrendingDown, Package, Globe, CreditCard, ArrowRight, Flame, ShoppingCart, Calendar, CheckCircle, Circle, Download, ChevronDown, HelpCircle, Wine } from 'lucide-react';
// Mock contact data - in real app, fetch from API
const mockContactData = {
  '1': {
    id: '1',
    name: 'Sarah Johnson',
    email: 'sarah.j@example.com',
    phone: '+1 (555) 123-4567',
    address: {
      street: '123 Main St',
      city: 'San Francisco',
      state: 'CA',
      zip: '94102',
      country: 'United States'
    },
    status: 'Active',
    subscriptionId: 'SUB-23144',
    subscriptionTitle: 'Premium Wine Club - 6 Pack',
    paymentStatus: 'Current',
    atRisk: false,
    tags: ['VIP', 'Red Wine'],
    memberSince: '2023-01-15',
    nextBilling: '2024-01-15',
    ltv: 1250.0,
    totalOrders: 12,
    subscriptionSpend: 1250.0,
    subscriptionOrders: 12,
    streak: 12,
    lastOrder: '2023-12-15',
    aov: 104.16,
    communicationPreferences: {
      emailMarketing: 'subscribed',
      smsMarketing: 'unsubscribed',
      transactional: 'subscribed'
    },
    paymentMethod: {
      type: 'Visa',
      last4: '4242',
      expiry: '01/26'
    },
    shopifyTags: ['Wholesale', 'Repeat Customer']
  }
};
interface ContactDetailPageProps {
  currentPage?: string;
  onNavigate?: (page: string) => void;
}
export function ContactDetailPage({
  currentPage = 'Contacts',
  onNavigate
}: ContactDetailPageProps) {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    getActivitiesByCustomer,
    addActivity
  } = useActivities();
  const contactActivities = getActivitiesByCustomer(id || '1');
  const {
    addTask
  } = useTasks();
  const {
    allTags,
    getCustomerTags,
    addTagToCustomer,
    removeTagFromCustomer,
    createTag
  } = useTags();
  const contactTags = getCustomerTags(id || '1');
  const contact = mockContactData[id as keyof typeof mockContactData];
  const actualSubscription = useMemo(() => {
    return subscriptions.find(sub => sub.id === contact?.subscriptionId);
  }, [contact?.subscriptionId]);
  const [isEditing, setIsEditing] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [phoneCopied, setPhoneCopied] = useState(false);
  const location = useLocation();
  const [activeTab, setActiveTab] = useState((location.state as any)?.activeTab || 'Overview');
  const [formData, setFormData] = useState({
    name: contact?.name || '',
    email: contact?.email || '',
    phone: contact?.phone || '',
    street: contact?.address.street || '',
    city: contact?.address.city || '',
    state: contact?.address.state || '',
    zip: contact?.address.zip || '',
    country: contact?.address.country || '',
    emailMarketing: contact?.communicationPreferences.emailMarketing || 'subscribed',
    smsMarketing: contact?.communicationPreferences.smsMarketing || 'unsubscribed',
    transactional: contact?.communicationPreferences.transactional || 'subscribed'
  });
  const [paymentMethod, setPaymentMethod] = useState(contact?.paymentMethod || {
    type: 'Visa',
    last4: '4242',
    expiry: '01/26'
  });
  const isCardExpiringSoon = (expiry: string) => {
    const [month, year] = expiry.split('/');
    const expiryDate = new Date(2000 + parseInt(year), parseInt(month) - 1);
    const today = new Date();
    const sixtyDaysFromNow = new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000);
    return expiryDate <= sixtyDaysFromNow && expiryDate >= today;
  };
  const isCardExpired = (expiry: string) => {
    const [month, year] = expiry.split('/');
    const expiryDate = new Date(2000 + parseInt(year), parseInt(month) - 1);
    const today = new Date();
    return expiryDate < today;
  };
  const handleNavigate = onNavigate || ((page: string) => {
    if (page === 'Contacts') navigate('/contacts');else navigate('/');
  });
  const handleCopyEmail = async () => {
    await navigator.clipboard.writeText(formData.email);
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2000);
  };
  const handleCopyPhone = async () => {
    await navigator.clipboard.writeText(formData.phone);
    setPhoneCopied(true);
    setTimeout(() => setPhoneCopied(false), 2000);
  };
  const handleSave = () => {
    console.log('Saving contact:', formData);
    setIsEditing(false);
  };
  const handleCancel = () => {
    setFormData({
      name: contact?.name || '',
      email: contact?.email || '',
      phone: contact?.phone || '',
      street: contact?.address.street || '',
      city: contact?.address.city || '',
      state: contact?.address.state || '',
      zip: contact?.address.zip || '',
      country: contact?.address.country || '',
      emailMarketing: contact?.communicationPreferences.emailMarketing || 'subscribed',
      smsMarketing: contact?.communicationPreferences.smsMarketing || 'unsubscribed',
      transactional: contact?.communicationPreferences.transactional || 'subscribed'
    });
    setIsEditing(false);
  };
  const handlePaymentUpdate = (paymentData: PaymentData) => {
    const firstDigit = paymentData.cardNumber.charAt(0);
    let cardType = 'Card';
    if (firstDigit === '4') cardType = 'Visa';else if (firstDigit === '5') cardType = 'Mastercard';else if (firstDigit === '3') cardType = 'Amex';
    const last4 = paymentData.cardNumber.replace(/\s/g, '').slice(-4);
    const expiry = `${paymentData.expiryMonth}/${paymentData.expiryYear}`;
    setPaymentMethod({
      type: cardType,
      last4,
      expiry
    });
  };
  const handleAddTag = (tag: TagData) => {
    addTagToCustomer(id || '1', tag);
  };
  const handleRemoveTag = (tagId: string) => {
    removeTagFromCustomer(id || '1', tagId);
  };
  const handleCreateTag = (label: string, color?: string) => {
    const newTag: TagData = {
      id: label.toLowerCase().replace(/\s+/g, '-'),
      label,
      color: (color || 'blue') as 'blue' | 'green' | 'orange' | 'purple' | 'gray'
    };
    createTag(newTag);
    addTagToCustomer(id || '1', newTag);
  };
  const handleAddNote = (noteText: string) => {
    addActivity(id || '1', {
      type: 'note',
      date: new Date().toLocaleDateString('en-GB').split('/').join('-'),
      title: 'Admin Note',
      description: noteText,
      author: 'Sarah Admin'
    });
  };
  const handleAddTask = (task: TaskData) => {
    addTask({
      title: task.title,
      assignee: {
        name: task.assignee,
        avatar: task.assignee.split(' ').map(n => n[0]).join(''),
        email: `${task.assignee.toLowerCase().replace(' ', '.')}@acmewine.com`
      },
      priority: task.priority as any,
      status: 'Open',
      dueDate: task.dueDate,
      linkedTo: {
        id: id || '1',
        type: 'contact',
        label: contact.name
      },
      createdBy: 'Sarah Chen'
    });
  };
  if (!contact) {
    return <div className="flex h-screen bg-[#F1F5F9] items-center justify-center" data-id="element-5366">
        <div className="text-center" data-id="element-5367">
          <h2 className="text-xl font-semibold text-[#0F172A] mb-2" data-id="element-5368">
            Contact not found
          </h2>
          <Button onClick={() => navigate('/contacts')} data-id="element-5369">
            Back to Contacts
          </Button>
        </div>
      </div>;
  }
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-5370">
      {/* Header */}
      <div className="mb-6" data-id="element-5371">
        <div className="flex items-center justify-between" data-id="element-5372">
          <div className="flex items-center gap-4" data-id="element-5373">
            <button onClick={() => navigate('/contacts')} className="flex items-center justify-center w-10 h-10 rounded-md border border-[#CBD5E1] bg-white hover:bg-[#F8FAFC] transition-colors" data-id="element-5374">
              <ArrowLeft className="w-5 h-5 text-[#475569]" data-id="element-5375" />
            </button>
            <div data-id="element-5376">
              <h1 className="text-2xl font-semibold text-[#0F172A]" data-id="element-5377">
                {formData.name}
              </h1>
              <p className="text-sm text-[#64748B] mt-1" data-id="element-5378">Contact Details</p>
            </div>
          </div>

          <div className="flex items-center gap-3" data-id="element-5379">
            {!isEditing ? <>
                <Button variant="outline" iconLeft={<ExternalLink className="w-4 h-4" data-id="element-5381" />} onClick={() => window.open(`https://admin.shopify.com/customers/${id}`, '_blank')} data-id="element-5380">
                  View in Shopify
                </Button>
                <Button variant="outline" iconLeft={<Package className="w-4 h-4" data-id="element-5383" />} onClick={() => navigate(`/subscriptions/${contact.subscriptionId}`)} data-id="element-5382">
                  View Subscription
                </Button>
                <Button variant="primary" iconLeft={<Edit className="w-4 h-4" data-id="element-5385" />} onClick={() => setIsEditing(true)} data-id="element-5384">
                  Edit
                </Button>
              </> : <>
                <Button variant="outline" onClick={handleCancel} data-id="element-5386">
                  Cancel
                </Button>
                <Button variant="primary" iconLeft={<Save className="w-4 h-4" data-id="element-5388" />} onClick={handleSave} data-id="element-5387">
                  Save Changes
                </Button>
              </>}
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="border-b border-[#E2E8F0] mb-6" data-id="element-5389">
        <div className="flex gap-6" data-id="element-5390">
          {['Overview', 'Subscriptions', 'Orders', 'Loyalty', 'Allocations'].map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 text-sm font-medium relative transition-colors ${activeTab === tab ? 'text-[#0EA5E9]' : 'text-[#475569] hover:text-[#0F172A]'}`} data-id="element-5391">
              {tab}
              {activeTab === tab && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0EA5E9] rounded-t-full" data-id="element-5392" />}
            </button>)}
        </div>
      </div>

      {/* Overview Tab Content */}
      {activeTab === 'Overview' && <div className="space-y-6" data-id="element-5393">
          {/* Contact Overview Stats - Full Width */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-5394">
            <StatCard label="Subscription Spend" value={`$${contact.subscriptionSpend.toFixed(2)}`} data-id="element-5395" />
            <StatCard label="Subscription Orders" value={contact.subscriptionOrders} data-id="element-5396" />
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-5 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-5397">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-[#94A3B8] uppercase tracking-wider mb-2" data-id="element-5398">
                Streak Count
                <div className="relative group" data-id="element-5399">
                  <HelpCircle className="w-3.5 h-3.5 text-[#94A3B8] cursor-help" data-id="element-5400" />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[#0F172A] text-white text-xs font-normal normal-case tracking-normal rounded-md shadow-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50" data-id="element-5401">
                    Consecutive subscription orders without a skip, pause, or
                    cancellation
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-[#0F172A]" data-id="element-5402" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2" data-id="element-5403">
                <div className="text-[28px] font-semibold text-[#0F172A]" data-id="element-5404">
                  {contact.streak}
                </div>
                <Flame className="w-5 h-5 text-[#F59E0B]" data-id="element-5405" />
              </div>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-5406">
            {/* Left Column - Main Content (2/3) */}
            <div className="lg:col-span-2 space-y-6" data-id="element-5407">
              {/* Customer Overview Card */}
              <Card padding="none" data-id="element-5408">
                <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-5409">
                  <h3 className="text-xl font-semibold text-[#0F172A]" data-id="element-5410">
                    Customer Overview
                  </h3>
                </div>

                <div className="p-6 bg-white space-y-5" data-id="element-5411">
                  {/* Name */}
                  <div className="flex items-start gap-3" data-id="element-5412">
                    <UserIcon className="w-[18px] h-[18px] text-[#94A3B8] flex-shrink-0 mt-0.5" data-id="element-5413" />
                    <div className="flex-1 min-w-0" data-id="element-5414">
                      {isEditing ? <Input value={formData.name} onChange={e => setFormData({
                    ...formData,
                    name: e.target.value
                  })} data-id="element-5415" /> : <span className="text-sm text-[#475569] break-words" data-id="element-5416">
                          {formData.name}
                        </span>}
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-3" data-id="element-5417">
                    <Mail className="w-[18px] h-[18px] text-[#94A3B8] flex-shrink-0 mt-0.5" data-id="element-5418" />
                    <div className="flex-1 min-w-0" data-id="element-5419">
                      {isEditing ? <Input type="email" value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} data-id="element-5420" /> : <span className="text-sm text-[#475569] break-all" data-id="element-5421">
                          {formData.email}
                        </span>}
                    </div>
                    {!isEditing && <button onClick={handleCopyEmail} className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-md hover:bg-[#F1F5F9] transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 active:scale-95" aria-label="Copy email address" data-id="element-5422">
                        {emailCopied ? <Check className="w-[18px] h-[18px] text-[#10B981]" data-id="element-5423" /> : <Copy className="w-[18px] h-[18px] text-[#94A3B8]" data-id="element-5424" />}
                      </button>}
                  </div>

                  {/* Phone */}
                  <div className="flex items-start gap-3" data-id="element-5425">
                    <Phone className="w-[18px] h-[18px] text-[#94A3B8] flex-shrink-0 mt-0.5" data-id="element-5426" />
                    <div className="flex-1 min-w-0" data-id="element-5427">
                      {isEditing ? <Input type="tel" value={formData.phone} onChange={e => setFormData({
                    ...formData,
                    phone: e.target.value
                  })} data-id="element-5428" /> : <span className="text-sm text-[#475569]" data-id="element-5429">
                          {formData.phone}
                        </span>}
                    </div>
                    {!isEditing && <button onClick={handleCopyPhone} className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-md hover:bg-[#F1F5F9] transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 active:scale-95" aria-label="Copy phone number" data-id="element-5430">
                        {phoneCopied ? <Check className="w-[18px] h-[18px] text-[#10B981]" data-id="element-5431" /> : <Copy className="w-[18px] h-[18px] text-[#94A3B8]" data-id="element-5432" />}
                      </button>}
                  </div>

                  {/* Address */}
                  <div className="flex items-start gap-3" data-id="element-5433">
                    <MapPin className="w-[18px] h-[18px] text-[#94A3B8] flex-shrink-0 mt-0.5" data-id="element-5434" />
                    <div className="flex-1 min-w-0" data-id="element-5435">
                      {isEditing ? <div className="space-y-3" data-id="element-5436">
                          <Input placeholder="Street address" value={formData.street} onChange={e => setFormData({
                      ...formData,
                      street: e.target.value
                    })} data-id="element-5437" />
                          <div className="grid grid-cols-2 gap-3" data-id="element-5438">
                            <Input placeholder="City" value={formData.city} onChange={e => setFormData({
                        ...formData,
                        city: e.target.value
                      })} data-id="element-5439" />
                            <Input placeholder="State" value={formData.state} onChange={e => setFormData({
                        ...formData,
                        state: e.target.value
                      })} data-id="element-5440" />
                          </div>
                          <div className="grid grid-cols-2 gap-3" data-id="element-5441">
                            <Input placeholder="ZIP" value={formData.zip} onChange={e => setFormData({
                        ...formData,
                        zip: e.target.value
                      })} data-id="element-5442" />
                            <Input placeholder="Country" value={formData.country} onChange={e => setFormData({
                        ...formData,
                        country: e.target.value
                      })} data-id="element-5443" />
                          </div>
                        </div> : <div className="text-sm text-[#475569] leading-relaxed" data-id="element-5444">
                          <div data-id="element-5445">{formData.street}</div>
                          <div data-id="element-5446">
                            {formData.city}, {formData.state} {formData.zip}
                          </div>
                          <div data-id="element-5447">{formData.country}</div>
                        </div>}
                    </div>
                  </div>
                </div>
              </Card>

              {/* Activity Timeline */}
              <Card padding="none" data-id="element-5448">
                <ActivityTimeline events={contactActivities} onAddNote={handleAddNote} onAddTask={() => setIsCreateTaskModalOpen(true)} data-id="element-5449" />
              </Card>
            </div>

            {/* Right Column - Sidebar (1/3) */}
            <div className="space-y-6" data-id="element-5450">
              {/* Payment Method Card */}
              <Card padding="none" data-id="element-5451">
                <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-5452">
                  <div className="flex items-center justify-between" data-id="element-5453">
                    <h3 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-5454">
                      Payment Method
                    </h3>
                    <button onClick={() => setIsPaymentModalOpen(true)} className="text-[13px] text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors" data-id="element-5455">
                      Update
                    </button>
                  </div>
                </div>

                <div className="p-6 bg-white" data-id="element-5456">
                  <div className="flex items-center gap-3 bg-[#F8FAFC] p-3 rounded-md border border-[#E2E8F0]" data-id="element-5457">
                    <CreditCard className="w-5 h-5 text-[#475569]" data-id="element-5458" />
                    <div className="flex-1" data-id="element-5459">
                      <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-5460">
                        {paymentMethod.type} ending in {paymentMethod.last4}
                      </div>
                      <div className="flex items-center gap-2 mt-1" data-id="element-5461">
                        <span className="text-[12px] text-[#94A3B8]" data-id="element-5462">
                          Expires {paymentMethod.expiry}
                        </span>
                        {isCardExpired(paymentMethod.expiry) && <span className="inline-flex items-center px-2 py-0.5 rounded text-[12px] font-medium bg-[#FEE2E2] text-[#DC2626] border border-[#FECACA]" data-id="element-5463">
                            Expired
                          </span>}
                        {!isCardExpired(paymentMethod.expiry) && isCardExpiringSoon(paymentMethod.expiry) && <span className="inline-flex items-center px-2 py-0.5 rounded text-[12px] font-medium bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A]" data-id="element-5464">
                              Expiring Soon
                            </span>}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Communication Preferences Card */}
              <Card padding="none" data-id="element-5465">
                <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-5466">
                  <h3 className="text-xl font-semibold text-[#0F172A]" data-id="element-5467">
                    Communication Preferences
                  </h3>
                </div>

                <div className="p-6 bg-white space-y-4" data-id="element-5468">
                  {/* Email Marketing */}
                  <div className="flex items-center justify-between" data-id="element-5469">
                    <span className="text-sm font-medium text-[#475569]" data-id="element-5470">
                      Email Marketing
                    </span>
                    {isEditing ? <select value={formData.emailMarketing} onChange={e => setFormData({
                  ...formData,
                  emailMarketing: e.target.value
                })} className="text-sm border border-[#CBD5E1] rounded px-2 py-1" data-id="element-5471">
                        <option value="subscribed" data-id="element-5472">Subscribed</option>
                        <option value="unsubscribed" data-id="element-5473">Unsubscribed</option>
                        <option value="never" data-id="element-5474">Never Subscribed</option>
                      </select> : <Badge variant={formData.emailMarketing === 'subscribed' ? 'success' : formData.emailMarketing === 'unsubscribed' ? 'error' : 'default'} data-id="element-5475">
                        {formData.emailMarketing === 'subscribed' ? 'Subscribed' : formData.emailMarketing === 'unsubscribed' ? 'Unsubscribed' : 'Never Subscribed'}
                      </Badge>}
                  </div>

                  {/* SMS Marketing */}
                  <div className="flex items-center justify-between" data-id="element-5476">
                    <span className="text-sm font-medium text-[#475569]" data-id="element-5477">
                      SMS Marketing
                    </span>
                    {isEditing ? <select value={formData.smsMarketing} onChange={e => setFormData({
                  ...formData,
                  smsMarketing: e.target.value
                })} className="text-sm border border-[#CBD5E1] rounded px-2 py-1" data-id="element-5478">
                        <option value="subscribed" data-id="element-5479">Subscribed</option>
                        <option value="unsubscribed" data-id="element-5480">Unsubscribed</option>
                        <option value="never" data-id="element-5481">Never Subscribed</option>
                      </select> : <Badge variant={formData.smsMarketing === 'subscribed' ? 'success' : formData.smsMarketing === 'unsubscribed' ? 'error' : 'default'} data-id="element-5482">
                        {formData.smsMarketing === 'subscribed' ? 'Subscribed' : formData.smsMarketing === 'unsubscribed' ? 'Unsubscribed' : 'Never Subscribed'}
                      </Badge>}
                  </div>

                  {/* Transactional */}
                  <div className="flex items-center justify-between" data-id="element-5483">
                    <span className="text-sm font-medium text-[#475569]" data-id="element-5484">
                      Transactional
                    </span>
                    {isEditing ? <select value={formData.transactional} onChange={e => setFormData({
                  ...formData,
                  transactional: e.target.value
                })} className="text-sm border border-[#CBD5E1] rounded px-2 py-1" data-id="element-5485">
                        <option value="subscribed" data-id="element-5486">Subscribed</option>
                        <option value="unsubscribed" data-id="element-5487">Unsubscribed</option>
                      </select> : <Badge variant={formData.transactional === 'subscribed' ? 'success' : 'error'} data-id="element-5488">
                        {formData.transactional === 'subscribed' ? 'Subscribed' : 'Unsubscribed'}
                      </Badge>}
                  </div>
                </div>
              </Card>

              {/* Tags Card */}
              <Card padding="none" data-id="element-5489">
                <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-5490">
                  <h3 className="text-xl font-semibold text-[#0F172A]" data-id="element-5491">Tags</h3>
                </div>

                <div className="p-6 bg-white space-y-4" data-id="element-5492">
                  {/* App Tags */}
                  <div data-id="element-5493">
                    <label className="block text-sm font-medium text-[#475569] mb-2" data-id="element-5494">
                      App Tags
                    </label>
                    <TagInput tags={contactTags} availableTags={allTags} onAddTag={handleAddTag} onRemoveTag={handleRemoveTag} onCreateTag={handleCreateTag} placeholder="Add or create tag..." data-id="element-5495" />
                  </div>

                  {/* Shopify Tags (Read-only) */}
                  <div data-id="element-5496">
                    <label className="block text-sm font-medium text-[#475569] mb-2" data-id="element-5497">
                      Shopify Tags
                    </label>
                    <div className="flex flex-wrap gap-2" data-id="element-5498">
                      {contact.shopifyTags.map(tag => <span key={tag} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-[#F1F5F9] text-[#475569] border border-[#E2E8F0]" data-id="element-5499">
                          {tag}
                        </span>)}
                    </div>
                    <p className="text-xs text-[#94A3B8] mt-2" data-id="element-5500">
                      Synced from Shopify
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>}

      {/* Loyalty Tab Content */}
      {activeTab === 'Loyalty' && <div className="space-y-6" data-id="element-5501">
          {/* Row 1: Summary & Progress */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-5502">
            {/* Loyalty Summary */}
            <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden" data-id="element-5503">
              <div className="p-6 border-b border-[#E2E8F0]" data-id="element-5504">
                <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-5505">
                  Loyalty Summary
                </h3>
              </div>
              <div className="p-6" data-id="element-5506">
                <div className="mb-6" data-id="element-5507">
                  <div className="text-3xl font-bold text-[#0F172A]" data-id="element-5508">4,250</div>
                  <div className="text-sm text-[#64748B]" data-id="element-5509">points</div>
                </div>
                <div className="space-y-3" data-id="element-5510">
                  <div className="flex justify-between items-center" data-id="element-5511">
                    <span className="text-sm text-[#475569]" data-id="element-5512">Tier</span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#EDE9FE] text-[#6F42C1]" data-id="element-5513">
                      Sommelier
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-5514">
                    <span className="text-sm text-[#475569]" data-id="element-5515">Wallet Pass</span>
                    <div className="flex items-center gap-1.5" data-id="element-5516">
                      <CheckCircle className="w-4 h-4 text-[#10B981]" data-id="element-5517" />
                      <span className="text-sm font-medium text-[#10B981]" data-id="element-5518">
                        Downloaded
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-5519">
                    <span className="text-sm text-[#475569]" data-id="element-5520">Status</span>
                    <span className="text-sm font-medium text-[#0F172A]" data-id="element-5521">
                      Active
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-5522">
                    <span className="text-sm text-[#475569]" data-id="element-5523">Member Since</span>
                    <span className="text-sm font-medium text-[#0F172A]" data-id="element-5524">
                      March 2025
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 mt-6" data-id="element-5525">
                  <button className="h-9 px-3 bg-white border border-[#CBD5E1] text-[#0F172A] rounded-md text-sm font-medium hover:bg-[#F8FAFC] transition-colors" data-id="element-5526">
                    Adjust Points
                  </button>
                  <button className="h-9 px-3 bg-white border border-[#CBD5E1] text-[#0F172A] rounded-md text-sm font-medium hover:bg-[#F8FAFC] transition-colors" data-id="element-5527">
                    Override Tier
                  </button>
                  <button className="h-9 px-3 text-[#0EA5E9] hover:bg-[#E0F2FE] rounded-md text-sm font-medium transition-colors" data-id="element-5528">
                    Resend Pass
                  </button>
                </div>
              </div>
            </div>

            {/* Tier Progress */}
            <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden" data-id="element-5529">
              <div className="p-6 border-b border-[#E2E8F0]" data-id="element-5530">
                <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-5531">
                  Tier Progress
                </h3>
              </div>
              <div className="p-6" data-id="element-5532">
                <div className="mb-6" data-id="element-5533">
                  <div className="flex justify-between text-sm font-medium text-[#0F172A] mb-2" data-id="element-5534">
                    <span data-id="element-5535">Sommelier</span>
                    <span data-id="element-5536">Vintner</span>
                  </div>
                  <div className="h-3 bg-[#E2E8F0] rounded-full overflow-hidden" data-id="element-5537">
                    <div className="h-full bg-[#6F42C1] rounded-full" style={{
                  width: '85%'
                }} data-id="element-5538" />
                  </div>
                  <p className="text-xs text-[#64748B] mt-2" data-id="element-5539">
                    $750 more annual spend to reach Vintner
                  </p>
                </div>

                <div data-id="element-5540">
                  <h4 className="text-sm font-semibold text-[#0F172A] mb-3" data-id="element-5541">
                    Tier History
                  </h4>
                  <div className="space-y-3" data-id="element-5542">
                    {[{
                  tier: 'Sommelier',
                  date: 'January 2026',
                  color: '#6F42C1'
                }, {
                  tier: 'Connoisseur',
                  date: 'September 2025',
                  color: '#3B82F6'
                }, {
                  tier: 'Enthusiast',
                  date: 'March 2025',
                  color: '#10B981'
                }].map(item => <div key={item.tier} className="flex items-center gap-3" data-id="element-5543">
                        <div className="w-2 h-2 rounded-full" style={{
                    backgroundColor: item.color
                  }} data-id="element-5544" />
                        <span className="text-sm font-medium text-[#0F172A]" data-id="element-5545">
                          {item.tier}
                        </span>
                        <span className="text-sm text-[#64748B]" data-id="element-5546">
                          {item.date}
                        </span>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Points History */}
          <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden" data-id="element-5547">
            <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-5548">
              <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-5549">
                Points History
              </h3>
              <div className="flex items-center gap-3" data-id="element-5550">
                <div className="relative" data-id="element-5551">
                  <select className="h-8 pl-2 pr-8 text-xs border border-[#CBD5E1] rounded-md bg-white focus:outline-none focus:border-[#0EA5E9] appearance-none" data-id="element-5552">
                    <option data-id="element-5553">All Types</option>
                    <option data-id="element-5554">Earned</option>
                    <option data-id="element-5555">Redeemed</option>
                    <option data-id="element-5556">Bonus</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[#94A3B8] pointer-events-none" data-id="element-5557" />
                </div>
                <div className="relative" data-id="element-5558">
                  <select className="h-8 pl-2 pr-8 text-xs border border-[#CBD5E1] rounded-md bg-white focus:outline-none focus:border-[#0EA5E9] appearance-none" data-id="element-5559">
                    <option data-id="element-5560">All Time</option>
                    <option data-id="element-5561">Last 30 Days</option>
                    <option data-id="element-5562">Last 90 Days</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[#94A3B8] pointer-events-none" data-id="element-5563" />
                </div>
                <button className="w-8 h-8 flex items-center justify-center rounded-md border border-[#CBD5E1] hover:bg-[#F1F5F9] transition-colors text-[#64748B]" data-id="element-5564">
                  <Download className="w-4 h-4" data-id="element-5565" />
                </button>
              </div>
            </div>
            <div className="overflow-x-auto" data-id="element-5566">
              <table className="w-full" data-id="element-5567">
                <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]" data-id="element-5568">
                  <tr data-id="element-5569">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#94A3B8] uppercase tracking-wider" data-id="element-5570">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#94A3B8] uppercase tracking-wider" data-id="element-5571">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#94A3B8] uppercase tracking-wider" data-id="element-5572">
                      Source
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#94A3B8] uppercase tracking-wider" data-id="element-5573">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#94A3B8] uppercase tracking-wider" data-id="element-5574">
                      Balance
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-5575">
                  {[{
                date: 'Feb 10, 2026',
                type: 'Earned',
                source: 'Order #4521',
                amount: '+150',
                balance: '4,250',
                color: '#10B981'
              }, {
                date: 'Feb 3, 2026',
                type: 'Redeemed',
                source: 'Free Shipping reward',
                amount: '-250',
                balance: '4,100',
                color: '#EF4444'
              }, {
                date: 'Jan 28, 2026',
                type: 'Earned',
                source: 'Cellar Door Visit',
                amount: '+100',
                balance: '4,350',
                color: '#10B981'
              }, {
                date: 'Jan 22, 2026',
                type: 'Earned',
                source: 'Order #4488',
                amount: '+320',
                balance: '4,250',
                color: '#10B981'
              }, {
                date: 'Jan 15, 2026',
                type: 'Bonus',
                source: 'Birthday',
                amount: '+200',
                balance: '3,930',
                color: '#F59E0B'
              }, {
                date: 'Jan 8, 2026',
                type: 'Earned',
                source: 'Order #4456',
                amount: '+280',
                balance: '3,730',
                color: '#10B981'
              }].map((row, i) => <tr key={i} className="hover:bg-[#F8FAFC]" data-id="element-5576">
                      <td className="px-6 py-4 text-sm text-[#0F172A]" data-id="element-5577">
                        {row.date}
                      </td>
                      <td className="px-6 py-4" data-id="element-5578">
                        <div className="flex items-center gap-2" data-id="element-5579">
                          <div className="w-2 h-2 rounded-full" style={{
                      backgroundColor: row.color
                    }} data-id="element-5580" />
                          <span className="text-sm text-[#0F172A]" data-id="element-5581">
                            {row.type}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#0F172A]" data-id="element-5582">
                        {row.source}
                      </td>
                      <td className={`px-6 py-4 text-sm font-medium text-right ${row.amount.startsWith('+') ? 'text-[#10B981]' : 'text-[#EF4444]'}`} data-id="element-5583">
                        {row.amount}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#64748B] text-right" data-id="element-5584">
                        {row.balance}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>

          {/* Row 3: Rewards & Check-ins */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-5585">
            {/* Available Rewards */}
            <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden" data-id="element-5586">
              <div className="p-6 border-b border-[#E2E8F0]" data-id="element-5587">
                <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-5588">
                  Available Rewards
                </h3>
              </div>
              <div className="divide-y divide-[#E2E8F0]" data-id="element-5589">
                {[{
              name: 'Free Shipping',
              points: '250 pts',
              available: true
            }, {
              name: '$10 Off',
              points: '1,000 pts',
              available: true
            }, {
              name: 'Free Tasting',
              points: '1,500 pts',
              available: true
            }, {
              name: 'Free Rosé Bottle',
              points: '2,500 pts',
              available: false,
              needed: '750 pts away'
            }].map(reward => <div key={reward.name} className="px-6 py-4 flex items-center gap-3" data-id="element-5590">
                    {reward.available ? <CheckCircle className="w-5 h-5 text-[#10B981]" data-id="element-5591" /> : <Circle className="w-5 h-5 text-[#CBD5E1]" data-id="element-5592" />}
                    <span className="text-sm font-medium text-[#0F172A]" data-id="element-5593">
                      {reward.name}
                    </span>
                    <span className="text-sm text-[#64748B]" data-id="element-5594">
                      {reward.points}
                    </span>
                    {!reward.available && <span className="text-xs text-[#94A3B8] ml-auto" data-id="element-5595">
                        {reward.needed}
                      </span>}
                  </div>)}
              </div>
            </div>

            {/* Check-In History */}
            <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden" data-id="element-5596">
              <div className="p-6 border-b border-[#E2E8F0]" data-id="element-5597">
                <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-5598">
                  Check-In History
                </h3>
              </div>
              <div className="divide-y divide-[#E2E8F0]" data-id="element-5599">
                {[{
              date: 'Feb 8, 2026',
              location: 'Main Cellar Door',
              type: 'Standard Tasting',
              points: '+100 pts'
            }, {
              date: 'Jan 15, 2026',
              location: 'Main Cellar Door',
              type: 'Premium Tasting',
              points: '+200 pts'
            }, {
              date: 'Dec 20, 2025',
              location: 'City Tasting Room',
              type: 'Vineyard Tour',
              points: '+150 pts'
            }].map((checkin, i) => <div key={i} className="px-6 py-4" data-id="element-5600">
                    <div className="text-xs text-[#94A3B8] mb-1" data-id="element-5601">
                      {checkin.date}
                    </div>
                    <div className="flex justify-between items-center" data-id="element-5602">
                      <span className="text-sm text-[#0F172A]" data-id="element-5603">
                        {checkin.location} — {checkin.type}
                      </span>
                      <span className="text-sm font-medium text-[#10B981]" data-id="element-5604">
                        {checkin.points}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>}

      {/* Allocations Tab Content */}
      {activeTab === 'Allocations' && <div className="space-y-8" data-id="element-5605">
          {/* Active Allocations Section */}
          <div data-id="element-5606">
            <div className="mb-4" data-id="element-5607">
              <h3 className="text-lg font-semibold text-[#0F172A]" data-id="element-5608">
                Active Allocations
              </h3>
              <p className="text-sm text-[#64748B]" data-id="element-5609">
                Allocations currently available to this member
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5610">
              {/* Card 1 */}
              <div onClick={() => navigate('/allocations/1')} className="bg-white border border-[#E2E8F0] rounded-lg p-5 shadow-sm hover:shadow-md hover:border-[#0EA5E9] transition-all cursor-pointer" data-id="element-5611">
                <div className="flex justify-between items-start mb-4" data-id="element-5612">
                  <div data-id="element-5613">
                    <span className="text-base font-bold text-[#0F172A] block mb-1" data-id="element-5614">
                      2023 Reserve Shiraz Release
                    </span>
                    <div className="text-sm text-[#64748B]" data-id="element-5615">
                      1 Mar – 15 Mar 2026
                    </div>
                  </div>
                  <Badge variant="success" data-id="element-5616">Active</Badge>
                </div>

                <div className="flex items-center gap-4 mb-4" data-id="element-5617">
                  <div className="flex -space-x-2" data-id="element-5618">
                    <div className="w-8 h-8 rounded-full bg-[#F1F5F9] border-2 border-white flex items-center justify-center text-[#94A3B8]" data-id="element-5619">
                      <Wine className="w-4 h-4" data-id="element-5620" />
                    </div>
                  </div>
                  <span className="text-sm text-[#475569]" data-id="element-5621">1 wine</span>
                </div>

                <div className="border-t border-[#F1F5F9] pt-4" data-id="element-5622">
                  <div className="flex justify-between text-xs mb-1.5" data-id="element-5623">
                    <span className="font-medium text-[#0F172A]" data-id="element-5624">
                      4 of 6 remaining
                    </span>
                    <span className="text-[#64748B]" data-id="element-5625">33% claimed</span>
                  </div>
                  <div className="h-1.5 w-full bg-[#E2E8F0] rounded-full overflow-hidden" data-id="element-5626">
                    <div className="h-full bg-[#10B981] rounded-full" style={{
                  width: '33%'
                }} data-id="element-5627" />
                  </div>
                </div>
              </div>

              {/* Card 2 */}
              <div onClick={() => navigate('/allocations/5')} className="bg-white border border-[#E2E8F0] rounded-lg p-5 shadow-sm hover:shadow-md hover:border-[#0EA5E9] transition-all cursor-pointer" data-id="element-5628">
                <div className="flex justify-between items-start mb-4" data-id="element-5629">
                  <div data-id="element-5630">
                    <span className="text-base font-bold text-[#0F172A] block mb-1" data-id="element-5631">
                      Vintner Exclusive — Museum Release
                    </span>
                    <div className="text-sm text-[#64748B]" data-id="element-5632">
                      5 Mar – 30 Mar 2026
                    </div>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F3E8FF] text-[#7E22CE]" data-id="element-5633">
                    Early Access
                  </span>
                </div>

                <div className="flex items-center gap-4 mb-4" data-id="element-5634">
                  <div className="flex -space-x-2" data-id="element-5635">
                    <div className="w-8 h-8 rounded-full bg-[#F1F5F9] border-2 border-white flex items-center justify-center text-[#94A3B8]" data-id="element-5636">
                      <Wine className="w-4 h-4" data-id="element-5637" />
                    </div>
                    <div className="w-8 h-8 rounded-full bg-[#F1F5F9] border-2 border-white flex items-center justify-center text-[#94A3B8]" data-id="element-5638">
                      <Wine className="w-4 h-4" data-id="element-5639" />
                    </div>
                  </div>
                  <span className="text-sm text-[#475569]" data-id="element-5640">2 wines</span>
                </div>

                <div className="border-t border-[#F1F5F9] pt-4" data-id="element-5641">
                  <div className="flex justify-between text-xs mb-1.5" data-id="element-5642">
                    <span className="font-medium text-[#0F172A]" data-id="element-5643">
                      2 of 2 remaining
                    </span>
                    <span className="text-[#64748B]" data-id="element-5644">0% claimed</span>
                  </div>
                  <div className="h-1.5 w-full bg-[#E2E8F0] rounded-full overflow-hidden" data-id="element-5645">
                    <div className="h-full bg-[#CBD5E1] rounded-full" style={{
                  width: '0%'
                }} data-id="element-5646" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Allocation History Section */}
          <div data-id="element-5647">
            <div className="mb-4" data-id="element-5648">
              <h3 className="text-lg font-semibold text-[#0F172A]" data-id="element-5649">
                Allocation History
              </h3>
              <p className="text-sm text-[#64748B]" data-id="element-5650">
                Past allocations this member was eligible for
              </p>
            </div>

            <Card padding="none" data-id="element-5651">
              <div className="overflow-x-auto" data-id="element-5652">
                <table className="w-full" data-id="element-5653">
                  <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]" data-id="element-5654">
                    <tr data-id="element-5655">
                      <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-5656">
                        Allocation
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-5657">
                        Date
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-5658">
                        Products
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-5659">
                        Purchased
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-5660">
                        Spent
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-5661">
                        Outcome
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]" data-id="element-5662">
                    {[{
                  id: '4',
                  name: 'Summer Rosé Pack',
                  date: 'Dec 1 – Dec 20, 2025',
                  products: 3,
                  purchased: 3,
                  spent: '$180.00',
                  outcome: 'Purchased',
                  variant: 'success'
                }, {
                  id: '3',
                  name: '2022 Single Vineyard Pinot',
                  date: 'Jan 10 – Jan 25, 2026',
                  products: 1,
                  purchased: 6,
                  spent: '$720.00',
                  outcome: 'Purchased',
                  variant: 'success'
                }, {
                  id: '7',
                  name: 'Autumn Library Collection',
                  date: 'Apr 1 – Apr 15, 2025',
                  products: 4,
                  purchased: 2,
                  spent: '$240.00',
                  outcome: 'Partially Claimed',
                  variant: 'warning'
                }, {
                  id: '8',
                  name: '2024 Chardonnay Pre-Release',
                  date: 'Nov 5 – Nov 20, 2025',
                  products: 1,
                  purchased: 0,
                  spent: '$0.00',
                  outcome: 'Declined',
                  variant: 'default'
                }, {
                  id: '9',
                  name: 'Holiday Magnum Special',
                  date: 'Dec 5 – Dec 15, 2025',
                  products: 2,
                  purchased: 0,
                  spent: '$0.00',
                  outcome: 'Expired',
                  variant: 'error'
                }].map((row, i) => <tr key={i} className="hover:bg-[#F8FAFC]" data-id="element-5663">
                        <td className="px-6 py-4" data-id="element-5664">
                          <button onClick={() => navigate(`/allocations/${row.id}`)} className="text-sm font-medium text-[#0EA5E9] hover:underline text-left" data-id="element-5665">
                            {row.name}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#64748B]" data-id="element-5666">
                          {row.date}
                        </td>
                        <td className="px-6 py-4 text-sm text-[#0F172A] text-center" data-id="element-5667">
                          {row.products}
                        </td>
                        <td className="px-6 py-4 text-sm text-[#0F172A] text-center" data-id="element-5668">
                          {row.purchased}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-[#0F172A] text-right" data-id="element-5669">
                          {row.spent}
                        </td>
                        <td className="px-6 py-4" data-id="element-5670">
                          <Badge variant={row.variant as any} data-id="element-5671">
                            {row.outcome}
                          </Badge>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </div>}

      {/* Subscriptions Tab Content */}
      {activeTab === 'Subscriptions' && <div className="space-y-6" data-id="element-5672">
          <Card padding="none" data-id="element-5673">
            <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-5674">
              <h3 className="text-xl font-semibold text-[#0F172A]" data-id="element-5675">
                Subscription
              </h3>
            </div>

            <div className="p-6 bg-white" data-id="element-5676">
              {contact.status !== 'Cancelled' ? <div className="space-y-4" data-id="element-5677">
                  <div className="flex items-start justify-between" data-id="element-5678">
                    <div className="flex-1" data-id="element-5679">
                      <div className="text-base font-semibold text-[#0F172A] mb-2" data-id="element-5680">
                        {actualSubscription?.subscriptionTitle || contact.subscriptionTitle}
                      </div>
                      <div className="flex items-center gap-3" data-id="element-5681">
                        <Badge variant={(actualSubscription?.status || contact.status) === 'Active' ? 'success' : (actualSubscription?.status || contact.status) === 'Paused' ? 'warning' : 'error'} data-id="element-5682">
                          {actualSubscription?.status || contact.status}
                        </Badge>
                        <span className="text-sm text-[#64748B]" data-id="element-5683">
                          Next billing:{' '}
                          {actualSubscription?.nextPayment || contact.nextBilling}
                        </span>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" iconRight={<ArrowRight className="w-4 h-4" data-id="element-5685" />} onClick={() => navigate(`/subscriptions/${contact.subscriptionId}`)} data-id="element-5684">
                      View Details
                    </Button>
                  </div>
                </div> : <div className="space-y-4" data-id="element-5686">
                  <div className="bg-[#FEE2E2] border border-[#FECACA] rounded-md p-4" data-id="element-5687">
                    <div className="flex items-start gap-3" data-id="element-5688">
                      <X className="w-5 h-5 text-[#DC2626] flex-shrink-0 mt-0.5" data-id="element-5689" />
                      <div data-id="element-5690">
                        <div className="font-semibold text-[#DC2626] mb-1" data-id="element-5691">
                          Cancelled on {contact.lastOrder}
                        </div>
                        <div className="text-sm text-[#991B1B]" data-id="element-5692">
                          Reason: Customer requested cancellation
                        </div>
                      </div>
                    </div>
                  </div>
                  <Button variant="primary" fullWidth data-id="element-5693">
                    Reactivate Subscription
                  </Button>
                </div>}
            </div>
          </Card>

          {/* Current Selection - Product Selector */}
          {actualSubscription && (() => {
        const subItems = actualSubscription.items || [];
        const subtotal = subItems.reduce((acc: number, item: any) => acc + getItemPrice(item.name) * item.quantity, 0);
        const discountPercent = actualSubscription.discountPercent || 0;
        const discountAmount = subtotal * discountPercent / 100;
        const total = subtotal - discountAmount;
        return <ProductSelector isEditing={false} formData={{
          preference: 'Custom',
          bottleCount: '3',
          frequency: 'Every Month'
        }} setFormData={() => {}} selectedProducts={{}} updateProductQuantity={() => {}} availableProducts={availableProducts} currentTotalBottles={subItems.reduce((acc: number, item: any) => acc + item.quantity, 0)} targetBottles={parseInt(actualSubscription.subscriptionTitle.match(/(\d+)/)?.[1] || '3')} isBottleCountCorrect={true} subscriptionItems={subItems} subtotal={subtotal} discountPercent={discountPercent} discountAmount={discountAmount} total={total} subscriptionTitle={actualSubscription.subscriptionTitle} data-id="element-5694" />;
      })()}
        </div>}

      {/* Orders Tab Content */}
      {activeTab === 'Orders' && <OrdersTable upcomingOrders={[{
      id: 'upcoming-1',
      date: contact.nextBilling,
      orderId: '#4012',
      chargeId: '#1210458823',
      amount: '$126.50',
      status: 'pending',
      chargeCreatedAt: 'Jan 10, 2024 at 8:00 AM',
      paymentProcessor: 'Shopify Payments',
      items: [{
        name: '2025 Fiano',
        quantity: 2,
        price: '$35.00'
      }, {
        name: '2024 Shiraz',
        quantity: 2,
        price: '$45.00'
      }],
      subtotal: '$160.00',
      taxes: '$16.00',
      shipping: '$0.00',
      total: '$176.00'
    }, {
      id: 'upcoming-2',
      date: '2024-02-15',
      orderId: '#4013',
      chargeId: '#1210458824',
      amount: '$142.00',
      status: 'pending',
      chargeCreatedAt: 'Feb 10, 2024 at 8:00 AM',
      paymentProcessor: 'Shopify Payments',
      items: [{
        name: '2024 Cabernet Sauvignon',
        quantity: 3,
        price: '$48.00'
      }, {
        name: '2025 Rosé',
        quantity: 3,
        price: '$32.00'
      }],
      subtotal: '$240.00',
      taxes: '$24.00',
      shipping: '$0.00',
      total: '$264.00'
    }, {
      id: 'upcoming-3',
      date: '2024-03-15',
      orderId: '#4014',
      chargeId: '#1210458825',
      amount: '$98.50',
      status: 'pending',
      chargeCreatedAt: 'Mar 10, 2024 at 8:00 AM',
      paymentProcessor: 'Shopify Payments',
      items: [{
        name: '2025 Sauvignon Blanc',
        quantity: 2,
        price: '$38.00'
      }, {
        name: '2024 Merlot',
        quantity: 2,
        price: '$42.00'
      }],
      subtotal: '$160.00',
      taxes: '$16.00',
      shipping: '$0.00',
      total: '$176.00'
    }, {
      id: 'upcoming-4',
      date: '2024-04-15',
      orderId: '#4015',
      chargeId: '#1210458826',
      amount: '$156.00',
      status: 'pending',
      chargeCreatedAt: 'Apr 10, 2024 at 8:00 AM',
      paymentProcessor: 'Shopify Payments',
      items: [{
        name: '2024 Pinot Noir',
        quantity: 4,
        price: '$52.00'
      }, {
        name: '2025 Chardonnay',
        quantity: 2,
        price: '$40.00'
      }],
      subtotal: '$288.00',
      taxes: '$28.80',
      shipping: '$0.00',
      total: '$316.80'
    }]} recentOrders={[{
      id: '1',
      date: contact.lastOrder,
      orderId: '#3873',
      chargeId: '#1180247974',
      amount: '$126.50',
      status: 'success',
      chargeCreatedAt: 'Dec 10, 2023 at 8:00 AM',
      paymentProcessor: 'Shopify Payments',
      items: [{
        name: '2025 Fiano',
        quantity: 2,
        price: '$35.00'
      }],
      subtotal: '$70.00',
      taxes: '$7.00',
      shipping: '$0.00',
      total: '$77.00'
    }, {
      id: '2',
      date: '2023-11-15',
      orderId: '#3872',
      chargeId: '#1180247973',
      amount: '$142.00',
      status: 'success',
      chargeCreatedAt: 'Nov 10, 2023 at 8:00 AM',
      paymentProcessor: 'Shopify Payments',
      items: [{
        name: '2024 Shiraz',
        quantity: 3,
        price: '$45.00'
      }, {
        name: '2024 Grenache',
        quantity: 3,
        price: '$38.00'
      }],
      subtotal: '$249.00',
      taxes: '$24.90',
      shipping: '$0.00',
      total: '$273.90'
    }, {
      id: '3',
      date: '2023-10-15',
      orderId: '#3871',
      chargeId: '#1180247972',
      amount: '$98.50',
      status: 'success',
      chargeCreatedAt: 'Oct 10, 2023 at 8:00 AM',
      paymentProcessor: 'Shopify Payments',
      items: [{
        name: '2024 Riesling',
        quantity: 2,
        price: '$36.00'
      }, {
        name: '2024 Tempranillo',
        quantity: 2,
        price: '$44.00'
      }],
      subtotal: '$160.00',
      taxes: '$16.00',
      shipping: '$0.00',
      total: '$176.00'
    }, {
      id: '4',
      date: '2023-09-15',
      orderId: '#3870',
      chargeId: '#1180247971',
      amount: '$134.00',
      status: 'success',
      chargeCreatedAt: 'Sep 10, 2023 at 8:00 AM',
      paymentProcessor: 'Shopify Payments',
      items: [{
        name: '2024 Cabernet Sauvignon',
        quantity: 2,
        price: '$48.00'
      }, {
        name: '2024 Pinot Grigio',
        quantity: 2,
        price: '$35.00'
      }, {
        name: '2024 Syrah',
        quantity: 2,
        price: '$46.00'
      }],
      subtotal: '$258.00',
      taxes: '$25.80',
      shipping: '$0.00',
      total: '$283.80'
    }]} data-id="element-5695" />}

      {/* Payment Method Modal */}
      <PaymentMethodModal isOpen={isPaymentModalOpen} onClose={() => setIsPaymentModalOpen(false)} onSave={handlePaymentUpdate} currentCard={paymentMethod} data-id="element-5696" />

      {/* Create Task Modal */}
      <CreateTaskModal isOpen={isCreateTaskModalOpen} onClose={() => setIsCreateTaskModalOpen(false)} onCreate={handleAddTask} initialLinkedTo={{
      id: contact.id,
      type: 'contact',
      label: contact.name
    }} data-id="element-5697" />
    </div>;
}